import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import ReelsPublishPanel from "./ReelsPublishPanel";
import AdsBoostPanel from "./AdsBoostPanel";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function MetaConnectPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/meta/config`, { withCredentials: true });
      setCfg(res.data);
    } catch {
      toast.error("Kunde inte hämta Meta-status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Läs query-params efter OAuth-callback
    const q = new URLSearchParams(window.location.search);
    if (q.get("meta_connected") === "1") {
      toast.success("Meta-konto anslutet!");
    } else if (q.get("meta_error")) {
      toast.error(`Meta-fel: ${q.get("meta_error")}`);
    }
    if (q.get("tab") === "meta" || q.get("meta_connected") || q.get("meta_error")) {
      // rensa querysträngen så toasten inte kommer flera gånger
      const url = new URL(window.location);
      url.search = "";
      window.history.replaceState({}, "", url);
    }
  }, [load]);

  const connect = () => {
    // Använd samma origin så cookies följer med
    window.location.href = `${API_BASE}/api/meta/oauth/start`;
  };

  const disconnect = async () => {
    if (!window.confirm("Koppla loss Meta-anslutningen? Ingen data raderas i Facebook/Instagram.")) return;
    setDisconnecting(true);
    try {
      await axios.delete(`${API_BASE}/api/meta/disconnect`, { withCredentials: true });
      toast.success("Frånkopplat");
      load();
    } catch {
      toast.error("Kunde inte koppla loss");
    } finally {
      setDisconnecting(false);
    }
  };

  if (!cfg) {
    return <div className="text-text-muted text-sm">Laddar…</div>;
  }

  const connected = cfg.connected;

  return (
    <div className="space-y-6" data-testid="meta-panel">
      <div className="bg-surface border border-app rounded-lg p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-heading text-lg">Meta / Reels Auto-Publisher</h2>
            <p className="text-sm text-text-muted mt-1">
              Anslut ditt Facebook-företag för att automatiskt publicera 9:16-reels
              till Instagram och Facebook varje dag.
            </p>
          </div>
          <button
            onClick={load}
            className="text-xs text-text-muted hover:text-text-primary underline"
            data-testid="meta-refresh"
          >
            Uppdatera
          </button>
        </div>

        {/* Environment / config-kontroll */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="meta-diag-grid">
          <Stat
            label="App ID"
            value={cfg.app_id_configured ? `…${cfg.app_id_preview} (${cfg.app_id_length} tkn)` : "✗ saknas"}
            error={!cfg.app_id_configured || cfg.app_id_dirty}
            warn={cfg.app_id_dirty ? 'Har citattecken/whitespace — rensa i deploy-env!' : null}
          />
          <Stat
            label="App Secret"
            value={cfg.app_secret_configured ? "✓ satt" : "✗ saknas"}
            error={!cfg.app_secret_configured || cfg.app_secret_dirty}
            warn={cfg.app_secret_dirty ? 'Har citattecken/whitespace — rensa i deploy-env!' : null}
          />
          <Stat
            label="Config ID"
            value={cfg.config_id_configured ? `…${cfg.config_id_preview}` : "✗ saknas"}
            error={!cfg.config_id_configured || cfg.config_id_dirty}
            warn={cfg.config_id_dirty ? 'Har citattecken/whitespace — rensa i deploy-env!' : null}
          />
          <Stat label="Graph API" value={cfg.graph_version} />
        </div>

        {(cfg.app_id_dirty || cfg.app_secret_dirty || cfg.config_id_dirty) && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-900" data-testid="meta-dirty-warn">
            <strong>⚠️ Fel format på env-variabel</strong> — värdet innehåller citattecken (t.ex. <code className="font-mono">"1401494..."</code>) eller whitespace. Öppna Emergent Deploy → Environment Variables och lägg in <em>endast siffrorna</em> utan citattecken. Re-deploya sedan.
          </div>
        )}

        {/* Anslutningsstatus */}
        {!connected ? (
          <div className="mt-6 border-t border-app pt-6">
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900 mb-4">
              <strong>Inte ansluten.</strong> Klicka nedan för att logga in med
              Facebook och godkänna behörigheter (pages_manage_posts, instagram_content_publish m.fl.).
              Tokens sparas krypterat i vår databas — inte i webbläsaren.
            </div>
            <button
              onClick={connect}
              disabled={loading || !cfg.app_id_configured || !cfg.app_secret_configured}
              className="bg-[#1877F2] hover:bg-[#1664c9] text-white font-medium rounded-md px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="meta-connect-btn"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Anslut Meta-konto
            </button>
            <p className="mt-3 text-xs text-text-muted">
              Redirect URI som måste finnas i Meta App → Facebook Login → Settings:{" "}
              <code className="bg-app px-1.5 py-0.5 rounded font-mono text-[11px] break-all">
                {`${API_BASE}/api/meta/oauth/callback`}
              </code>
            </p>
          </div>
        ) : (
          <div className="mt-6 border-t border-app pt-6 space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-4">
              <div className="text-emerald-900 font-semibold text-sm">✓ Ansluten</div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-emerald-900">
                <div>
                  <div className="text-xs uppercase tracking-widest text-emerald-700">Facebook-sida</div>
                  <div className="font-medium mt-0.5">{cfg.connected_page_name || "—"}</div>
                  <div className="font-mono text-[11px] text-emerald-700">{cfg.connected_page_id}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-emerald-700">Instagram Business</div>
                  <div className="font-medium mt-0.5">{cfg.connected_ig_username ? `@${cfg.connected_ig_username}` : "(ej kopplad till sidan)"}</div>
                  <div className="font-mono text-[11px] text-emerald-700">{cfg.connected_ig_id || "—"}</div>
                </div>
                <div className="col-span-full text-xs text-emerald-700">
                  Anslöts {cfg.connected_at ? new Date(cfg.connected_at).toLocaleString("sv-SE") : "—"}.
                  {cfg.token_expires_at && (
                    <>{" "}Access token utgår {new Date(cfg.token_expires_at).toLocaleDateString("sv-SE")}.</>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={connect}
                className="border border-app hover:bg-surface-hover rounded-md px-3 py-2 text-sm"
                data-testid="meta-reconnect"
              >
                Förnya token / byt sida
              </button>
              <button
                onClick={disconnect}
                disabled={disconnecting}
                className="border border-red-300 text-red-700 hover:bg-red-50 rounded-md px-3 py-2 text-sm disabled:opacity-50"
                data-testid="meta-disconnect"
              >
                {disconnecting ? "Kopplar loss…" : "Koppla loss"}
              </button>
            </div>
          </div>
        )}
      </div>
      <ReelsPublishPanel connected={!!cfg?.connected} />
      <AdsBoostPanel connected={!!cfg?.connected} />
    </div>
  );
}

function Stat({ label, value, error, warn }) {
  return (
    <div className="bg-app border border-app rounded p-3">
      <div className="text-text-muted text-xs uppercase tracking-widest">{label}</div>
      <div className={"font-mono mt-1 text-sm " + (error ? "text-red-600" : "")}>{value}</div>
      {warn && <div className="mt-1 text-[10px] text-red-700 leading-tight">{warn}</div>}
    </div>
  );
}
